import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState, ownerSelector } from "../../store/store";
import { logOut } from "../../store/slices/ownerSlice";
import { FaChevronDown, FaCrown } from "react-icons/fa6";
import { FiMenu } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import WorkSpaceSelectionModal from "../Modals/WorkSpaceSelectionModal";
import { Action, ThunkDispatch } from "@reduxjs/toolkit";
import {
  dismissNotification,
  fetchNotifications,
  fetchUnreadCount,
  getNotificationCampaignName,
  markNotificationRead,
  type NotificationItem,
} from "../../utils/notifications";
import { resolveBillingSource } from "../../utils/billingSource";
import {
  fetchGlobalSearch,
  resolveDashboardScope,
  type GlobalSearchResult,
} from "../../utils";
import { SearchInput } from "../ui";

type Props = {
  onMenuClick?: () => void;
};

const EMPTY_SEARCH: GlobalSearchResult = {
  lists: [],
  prospects: [],
  campaigns: [],
  senders: [],
};

const getInitials = (name?: string) => {
  const parts = (name || "").trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "IN";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
};

const Navbar = ({ onMenuClick }: Props) => {
  const dispatch: ThunkDispatch<RootState, undefined, Action> = useDispatch();
  const { owner, currentUser, currentWorkspace } = useSelector(ownerSelector);
  const [showProfileOptions, setShowProfileOptions] = useState(false);
  const [showWorkSpaceOptions, setShowWorkSpaceOptions] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notificationItems, setNotificationItems] = useState<NotificationItem[]>([]);
  const navigate = useNavigate();
  const [topQuery, setTopQuery] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchResults, setSearchResults] = useState<GlobalSearchResult>(EMPTY_SEARCH);
  const hasLinkedOwner = Boolean(owner?._id);
  const notifPopoverRef = useRef<HTMLDivElement | null>(null);
  const profilePopoverRef = useRef<HTMLDivElement | null>(null);
  const searchPopoverRef = useRef<HTMLDivElement | null>(null);
  const searchRequestIdRef = useRef(0);

  const refreshUnread = async () => {
    try {
      const res = await fetchUnreadCount();
      setUnreadCount(Number(res?.unread || 0));
    } catch {
      // ignore
    }
  };

  const refreshPreview = async () => {
    try {
      const res = await fetchNotifications({ limit: 6, offset: 0 });
      setNotificationItems(Array.isArray(res?.items) ? res.items : []);
    } catch {
      // ignore
    }
  };

  // Slow background poll only — primary refresh is on open / tab focus.
  useEffect(() => {
    void refreshUnread();
    const interval = window.setInterval(() => {
      if (document.visibilityState !== "visible") return;
      void refreshUnread();
    }, 180000);
    return () => window.clearInterval(interval);
  }, []);

  // Refresh when user returns to the tab (main actions / other devices may have created notifs).
  useEffect(() => {
    let debounceTimer: number | null = null;
    const scheduleRefresh = () => {
      if (document.visibilityState !== "visible") return;
      if (debounceTimer != null) window.clearTimeout(debounceTimer);
      debounceTimer = window.setTimeout(() => {
        void refreshUnread();
      }, 1000);
    };
    const onVisibility = () => {
      if (document.visibilityState === "visible") scheduleRefresh();
    };
    window.addEventListener("focus", scheduleRefresh);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      if (debounceTimer != null) window.clearTimeout(debounceTimer);
      window.removeEventListener("focus", scheduleRefresh);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, []);

  useEffect(() => {
    if (!showNotifications) return;
    void refreshUnread();
    void refreshPreview();
  }, [showNotifications]);

  useEffect(() => {
    const onMouseDown = (e: MouseEvent) => {
      if (!showNotifications) return;
      const target = e.target as HTMLElement | null;
      if (!target) return;
      if (notifPopoverRef.current?.contains(target)) return;
      setShowNotifications(false);
    };
    document.addEventListener("mousedown", onMouseDown);
    return () => document.removeEventListener("mousedown", onMouseDown);
  }, [showNotifications]);

  useEffect(() => {
    const onMouseDown = (e: MouseEvent) => {
      if (!showProfileOptions) return;
      const target = e.target as HTMLElement | null;
      if (!target) return;
      if (profilePopoverRef.current?.contains(target)) return;
      setShowProfileOptions(false);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (!showProfileOptions) return;
      if (e.key === "Escape") {
        setShowProfileOptions(false);
      }
    };
    document.addEventListener("mousedown", onMouseDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onMouseDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [showProfileOptions]);

  useEffect(() => {
    const onMouseDown = (e: MouseEvent) => {
      if (!searchOpen) return;
      const target = e.target as HTMLElement | null;
      if (!target) return;
      if (searchPopoverRef.current?.contains(target)) return;
      setSearchOpen(false);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (!searchOpen) return;
      if (e.key === "Escape") {
        setSearchOpen(false);
      }
    };
    document.addEventListener("mousedown", onMouseDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onMouseDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [searchOpen]);

  const visiblePreview = useMemo(
    () => notificationItems.filter((n) => !n.dismissedAt).slice(0, 5),
    [notificationItems]
  );
  const billingSource = resolveBillingSource({ owner, currentUser });
  const hasNoPlan = (billing: any) =>
    !billing || !Array.isArray(billing.planhistory) || billing.planhistory.length === 0;
  const hasFreePlanOnly = (billing: any) => {
    const ph = billing?.planhistory;
    if (!Array.isArray(ph) || ph.length === 0) return false;
    const last = ph[ph.length - 1];
    return last?.planName === "Free Plan";
  };
  const hasUsedTrial = (billing: any) => {
    const ph = billing?.planhistory;
    if (!Array.isArray(ph) || ph.length === 0) return false;
    return ph.some((p: any) => p?.isTrial === true);
  };
  const showActivateTrial =
    (hasNoPlan(billingSource) || hasFreePlanOnly(billingSource)) &&
    !hasUsedTrial(billingSource);

  const displayName = useMemo(() => {
    const first = String(currentUser?.firstName || "").trim();
    const last = String(currentUser?.lastName || "").trim();
    const full = [first, last].filter(Boolean).join(" ").trim();
    return full || String(owner?.name || "").trim() || "Account";
  }, [currentUser?.firstName, currentUser?.lastName, owner?.name]);

  const hasSearchHits = useMemo(
    () =>
      searchResults.lists.length > 0 ||
      searchResults.prospects.length > 0 ||
      searchResults.campaigns.length > 0 ||
      searchResults.senders.length > 0,
    [searchResults]
  );

  const clearSearch = useCallback(() => {
    setTopQuery("");
    setSearchResults(EMPTY_SEARCH);
    setSearchOpen(false);
    setSearchLoading(false);
  }, []);

  const handleTopSearch = (value: string) => {
    setTopQuery(value);
    if (!value.trim()) {
      setSearchResults(EMPTY_SEARCH);
      setSearchOpen(false);
      setSearchLoading(false);
    } else {
      setSearchOpen(true);
    }
  };

  const handleTopSearchDebounced = useCallback(
    async (value: string) => {
      const q = value.trim();
      if (!q) {
        setSearchResults(EMPTY_SEARCH);
        setSearchLoading(false);
        setSearchOpen(false);
        return;
      }
      if (!owner?._id) return;

      const requestId = ++searchRequestIdRef.current;
      setSearchLoading(true);
      setSearchOpen(true);
      try {
        const { resolvedUserId, resolvedWorkspaceId } = resolveDashboardScope(
          currentUser?.userId,
          currentWorkspace?._id
        );
        const results = await fetchGlobalSearch({
          ownerId: owner._id,
          userId: resolvedUserId,
          workspaceId: resolvedWorkspaceId,
          q,
          limit: 5,
        });
        if (requestId !== searchRequestIdRef.current) return;
        setSearchResults(results);
      } catch {
        if (requestId !== searchRequestIdRef.current) return;
        setSearchResults(EMPTY_SEARCH);
      } finally {
        if (requestId === searchRequestIdRef.current) {
          setSearchLoading(false);
        }
      }
    },
    [owner?._id, currentUser?.userId, currentWorkspace?._id]
  );

  const goToList = (listId: string) => {
    clearSearch();
    navigate(`/prospects/${listId}`);
  };

  const goToProspect = (name: string) => {
    clearSearch();
    const params = new URLSearchParams();
    if (name.trim()) params.set("q", name.trim());
    navigate(`/leads${params.toString() ? `?${params.toString()}` : ""}`);
  };

  const goToCampaign = (campaignId: string) => {
    clearSearch();
    navigate(`/campaign-management/view/${campaignId}`);
  };

  const goToSender = (name: string) => {
    clearSearch();
    const params = new URLSearchParams();
    if (name.trim()) params.set("q", name.trim());
    navigate(`/teams${params.toString() ? `?${params.toString()}` : ""}`);
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== "Enter") return;
    e.preventDefault();
    const q = topQuery.trim();
    if (!q) return;
    if (searchResults.lists[0]) {
      goToList(String(searchResults.lists[0]._id));
      return;
    }
    if (searchResults.prospects[0]) {
      goToProspect(searchResults.prospects[0].name || q);
      return;
    }
    if (searchResults.senders[0]) {
      goToSender(searchResults.senders[0].name || q);
      return;
    }
    if (searchResults.campaigns[0]) {
      goToCampaign(String(searchResults.campaigns[0]._id));
      return;
    }
    clearSearch();
    navigate(`/prospects?q=${encodeURIComponent(q)}`);
  };

  return (
    <>
      {/* Email gating is handled globally via the new modal in DefaultLayout. */}
      {owner?.usageError && (
        <div className=" p-2 text-xs  bg-mainColor/90 text-white flex justify-center items-center">
          <p>{owner?.usageError}</p>
        </div>
      )}
      <nav className="font-inter z-50 flex h-[72px] w-full min-w-0 items-center justify-between gap-4 border-b border-line bg-white px-4 sm:px-9">
        <div className="flex min-w-0 items-center gap-3">
          <button
            type="button"
            onClick={onMenuClick}
            className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-[8px] border border-line bg-white text-dark-blue lg:hidden"
            aria-label="Open menu"
          >
            <FiMenu className="h-5 w-5" aria-hidden="true" />
          </button>
          <div className="relative hidden w-[310px] shrink-0 md:block" ref={searchPopoverRef}>
            <SearchInput
              value={topQuery}
              onChange={handleTopSearch}
              onDebouncedChange={handleTopSearchDebounced}
              onFocus={() => {
                if (topQuery.trim()) setSearchOpen(true);
              }}
              onKeyDown={handleSearchKeyDown}
              variant="soft"
              placeholder="Search prospects, campaigns, and more"
              aria-label="Search prospects, campaigns, and more"
              aria-expanded={searchOpen}
              aria-controls="global-search-results"
              autoComplete="off"
              containerClassName="w-full"
            />
            {searchOpen && topQuery.trim() ? (
              <div
                id="global-search-results"
                role="listbox"
                className="absolute left-0 top-11 z-[60] w-[min(380px,calc(100vw-2rem))] overflow-hidden rounded-[10px] border border-light-grey bg-white shadow-[0_18px_40px_rgba(17,15,42,0.18)]"
              >
                <div className="max-h-[360px] overflow-auto">
                  {searchLoading && !hasSearchHits ? (
                    <div className="px-4 py-6 text-center text-sm text-dark-grey">
                      Searching…
                    </div>
                  ) : !hasSearchHits ? (
                    <div className="px-4 py-6 text-center">
                      <p className="text-sm font-medium text-dark-black">No results</p>
                      <p className="mt-1 text-xs text-dark-grey">
                        Try a list, prospect, or campaign name.
                      </p>
                    </div>
                  ) : (
                    <>
                      {searchResults.lists.length > 0 ? (
                        <div className="border-b border-light-grey py-2">
                          <p className="px-4 pb-1 text-[11px] font-semibold uppercase tracking-wide text-dark-grey">
                            Lists
                          </p>
                          {searchResults.lists.map((list) => (
                            <button
                              key={String(list._id)}
                              type="button"
                              role="option"
                              className="flex w-full items-center px-4 py-2 text-left hover:bg-background-grey/60"
                              onClick={() => goToList(String(list._id))}
                            >
                              <span className="truncate text-sm font-medium text-dark-black">
                                {list.listName}
                              </span>
                            </button>
                          ))}
                        </div>
                      ) : null}

                      {searchResults.prospects.length > 0 ? (
                        <div className="border-b border-light-grey py-2">
                          <p className="px-4 pb-1 text-[11px] font-semibold uppercase tracking-wide text-dark-grey">
                            Prospects
                          </p>
                          {searchResults.prospects.map((prospect) => (
                            <button
                              key={String(prospect._id)}
                              type="button"
                              role="option"
                              className="flex w-full items-start gap-2 px-4 py-2 text-left hover:bg-background-grey/60"
                              onClick={() => goToProspect(prospect.name)}
                            >
                              <span className="mt-0.5 grid h-7 w-7 shrink-0 place-items-center overflow-hidden rounded-full bg-user-avatar text-[10px] font-semibold text-user-avatar-fg">
                                {prospect.imageUrl ? (
                                  <img
                                    src={prospect.imageUrl}
                                    alt=""
                                    className="h-full w-full object-cover"
                                  />
                                ) : (
                                  getInitials(prospect.name)
                                )}
                              </span>
                              <span className="min-w-0">
                                <span className="block truncate text-sm font-medium text-dark-black">
                                  {prospect.name}
                                </span>
                                {prospect.company || prospect.headline ? (
                                  <span className="mt-0.5 block truncate text-xs text-dark-grey">
                                    {prospect.company || prospect.headline}
                                  </span>
                                ) : null}
                              </span>
                            </button>
                          ))}
                        </div>
                      ) : null}

                      {searchResults.senders.length > 0 ? (
                        <div className="border-b border-light-grey py-2">
                          <p className="px-4 pb-1 text-[11px] font-semibold uppercase tracking-wide text-dark-grey">
                            Senders
                          </p>
                          {searchResults.senders.map((sender) => (
                            <button
                              key={String(sender._id)}
                              type="button"
                              role="option"
                              className="flex w-full items-center gap-2 px-4 py-2 text-left hover:bg-background-grey/60"
                              onClick={() => goToSender(sender.name || sender.email || "")}
                            >
                              <span className="mt-0.5 grid h-7 w-7 shrink-0 place-items-center overflow-hidden rounded-full bg-user-avatar text-[10px] font-semibold text-user-avatar-fg">
                                {sender.imageUrl ? (
                                  <img
                                    src={sender.imageUrl}
                                    alt=""
                                    className="h-full w-full object-cover"
                                  />
                                ) : (
                                  getInitials(sender.name)
                                )}
                              </span>
                              <span className="min-w-0">
                                <span className="block truncate text-sm font-medium text-dark-black">
                                  {sender.name}
                                </span>
                                {sender.email ? (
                                  <span className="mt-0.5 block truncate text-xs text-dark-grey">
                                    {sender.email}
                                  </span>
                                ) : null}
                              </span>
                            </button>
                          ))}
                        </div>
                      ) : null}

                      {searchResults.campaigns.length > 0 ? (
                        <div className="py-2">
                          <p className="px-4 pb-1 text-[11px] font-semibold uppercase tracking-wide text-dark-grey">
                            Campaigns
                          </p>
                          {searchResults.campaigns.map((campaign) => (
                            <button
                              key={String(campaign._id)}
                              type="button"
                              role="option"
                              className="flex w-full items-center justify-between gap-2 px-4 py-2 text-left hover:bg-background-grey/60"
                              onClick={() => goToCampaign(String(campaign._id))}
                            >
                              <span className="truncate text-sm font-medium text-dark-black">
                                {campaign.campaignName}
                              </span>
                              {campaign.campaignStatus ? (
                                <span className="shrink-0 text-[10px] font-semibold uppercase text-dark-grey">
                                  {campaign.campaignStatus}
                                </span>
                              ) : null}
                            </button>
                          ))}
                        </div>
                      ) : null}
                    </>
                  )}
                </div>
                {topQuery.trim() ? (
                  <button
                    type="button"
                    className="w-full border-t border-light-grey bg-white px-4 py-2.5 text-center text-sm font-medium text-brand-blue hover:bg-light-grey/50"
                    onClick={() => {
                      const q = topQuery.trim();
                      clearSearch();
                      navigate(`/leads?q=${encodeURIComponent(q)}`);
                    }}
                  >
                    Search all in Leads Database
                  </button>
                ) : null}
              </div>
            ) : null}
          </div>
        </div>

        <div className="flex min-w-0 items-center justify-end gap-4 sm:gap-[17px]">
          {false && (
            <button
              onClick={() => setShowWorkSpaceOptions(true)}
              disabled={!hasLinkedOwner}
              className="flex h-[30px] items-center gap-6 rounded-[4px] border border-[#d9d9d9] bg-white px-3 text-sm text-[#0f1011]"
            >
              <span>{owner?.workspaces?.[0]?.name || "My first workspace"}</span>
              <FaChevronDown size={12} />
            </button>
          )}

          {showActivateTrial ? (
            <button
              onClick={() => navigate("/settings/billing")}
              className="flex h-9 items-center gap-1.5 rounded-[8px] border border-line bg-white px-2 text-sm font-semibold text-dark-blue sm:px-3"
            >
              <FaCrown className="text-warning" size={12} />
              <span className="hidden sm:inline">Please activate your trial</span>
              <span className="sm:hidden">Trial</span>
            </button>
          ) : null}

          <div className="relative" ref={notifPopoverRef}>
            <button
              type="button"
              onClick={() => setShowNotifications((p) => !p)}
              className="relative p-1 text-dark-blue"
              aria-label="Notifications"
              title="Notifications"
            >
              <img src="/icons/bell.svg" alt="" className="h-5 w-5" />
              {unreadCount > 0 ? (
                <span className="absolute right-0.5 top-0.5 h-1.5 w-1.5 rounded-full border-2 border-white bg-danger" />
              ) : null}
            </button>

            {showNotifications ? (
              <div className="absolute right-0 top-10 z-[60] w-[min(360px,calc(100vw-1.5rem))] overflow-hidden rounded-[10px] border border-light-grey bg-white shadow-[0_18px_40px_rgba(17,15,42,0.18)]">
                <div className="flex items-center justify-between border-b border-light-grey px-4 py-3">
                  <p className="text-sm font-semibold text-dark-black">Notifications</p>
                  <button
                    type="button"
                    onClick={() => setShowNotifications(false)}
                    className="rounded-md px-2 py-1 text-xs text-dark-grey hover:bg-light-grey/50"
                  >
                    Close
                  </button>
                </div>

                <div className="max-h-[340px] overflow-auto bg-background-grey/30">
                  {visiblePreview.length === 0 ? (
                    <div className="px-4 py-6 text-center">
                      <p className="text-sm font-medium text-dark-black">No notifications</p>
                      <p className="mt-1 text-xs text-dark-grey">You’re all caught up.</p>
                    </div>
                  ) : (
                    visiblePreview.map((n) => (
                      <div
                        key={n._id}
                        className="border-b border-light-grey bg-white px-4 py-3"
                      >
                        <div className="flex items-start justify-between gap-3">
                          <button
                            type="button"
                            className="min-w-0 flex-1 text-left"
                            onClick={async () => {
                              if (!n.readAt) {
                                setUnreadCount((c) => Math.max(0, c - 1));
                                await markNotificationRead(n._id).catch(() => {});
                              }
                              navigate("/notifications");
                              setShowNotifications(false);
                            }}
                          >
                            <p className="truncate text-sm font-semibold text-dark-black">
                              {n.title}
                            </p>
                            <p className="mt-0.5 line-clamp-2 text-xs text-dark-grey">
                              {n.message}
                            </p>
                            {getNotificationCampaignName(n) ? (
                              <p className="mt-1 truncate text-xs font-medium text-dark-black/80">
                                Campaign: {getNotificationCampaignName(n)}
                              </p>
                            ) : null}
                          </button>
                          <button
                            type="button"
                            onClick={async () => {
                              setNotificationItems((prev) => prev.filter((x) => x._id !== n._id));
                              await dismissNotification(n._id).catch(() => {});
                              void refreshUnread();
                            }}
                            className="rounded-md p-1.5 text-dark-grey hover:bg-light-grey/60"
                            aria-label="Dismiss"
                            title="Dismiss"
                          >
                            ✕
                          </button>
                        </div>
                      </div>
                    ))
                  )}
                </div>

                <button
                  type="button"
                  onClick={() => {
                    navigate("/notifications");
                    setShowNotifications(false);
                  }}
                  className="w-full border-t border-line bg-white px-4 py-3 text-center text-sm font-semibold text-dark-blue hover:bg-paper"
                >
                  See All Notifications
                </button>
              </div>
            ) : null}
          </div>

          <div className="relative flex items-center" ref={profilePopoverRef}>
            <button
              onClick={() => {
                setShowProfileOptions((previous) => !previous);
              }}
              className="flex items-center gap-2 rounded-md py-1 pl-1 pr-1.5"
            >
              <span className="grid h-[33px] w-[33px] place-items-center rounded-full bg-user-avatar text-[11px] font-semibold text-user-avatar-fg">
                {getInitials(displayName)}
              </span>
              <span className="hidden max-w-[140px] truncate text-sm font-semibold text-dark-blue sm:inline">
                {displayName}
              </span>
              <FaChevronDown className="hidden h-3 w-3 text-dark-blue sm:block" />
            </button>

            {showProfileOptions && (
              <div className="absolute right-0 top-9 flex min-w-[140px] flex-col gap-2 rounded-md border border-[#e7e7e7] bg-white p-3 shadow-lg">
                <button
                  onClick={() => dispatch(logOut())}
                  className="rounded-md bg-[#110f2a] p-2 text-[11px] text-white"
                >
                  Logout
                </button>
              </div>
            )}
          </div>
        </div>
      </nav>
      {false && showWorkSpaceOptions && (
        <WorkSpaceSelectionModal
          onClose={() => setShowWorkSpaceOptions(false)}
        />
      )}
    </>
  );
};

export default Navbar;
